import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { EMPTY_SEARCH_REGEX } from '@planone/bau365/util-search';
import { CookieConsentFacade } from '@planone/shared/feature-cookie-consent';
import { BehaviorSubject, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';

@Component({
  selector: 'planone-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
})
export class SearchInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  cacheKey = 'search';

  @Input()
  options: string[] | null = [];

  @Input()
  submittedSearchTerm = '';

  @Input()
  size: string | undefined | null;

  @Input()
  asYouTypeResultWithIcon = false;

  @Input()
  hasIconOnlyWithOverlayMobileTemplate = false;

  @Input()
  placeholderText: string | undefined;

  @Input()
  blurInput: boolean | null | undefined;

  @Input()
  sidebarOverlayToggled: boolean | null | undefined;

  @Output()
  searchFieldChanged: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  searchFieldSubmitted: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  searchFieldCleared: EventEmitter<boolean> = new EventEmitter<boolean>();

  scrollStrategy: ScrollStrategy;
  overlayIsOpen = false;
  searchField = new FormControl('');
  placeholderDefaultText = $localize`:@@ui-elements-search_default-placeholder:Suchen`;
  searchTerm = '';
  searchHistory: string[] = [];
  cookieConsent$: Observable<boolean | undefined>;

  private searchHistory$ = new BehaviorSubject<string[]>([]);
  private cookieConsentAccepted$: Observable<boolean | undefined>;
  private unsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('searchFieldInput') searchFieldInput: ElementRef<HTMLInputElement> | undefined;

  @ViewChild('searchFieldInputIconOnly', { static: false })
  set input(element: ElementRef<HTMLInputElement>) {
    if (element) {
      element.nativeElement.focus();
    }
  }

  constructor(
    cookieConsentFacade: CookieConsentFacade,
    scrollStrategies: ScrollStrategyOptions,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private cookieConsent: CookieConsentFacade,
    @Inject(EMPTY_SEARCH_REGEX) private emptySearchRegex: RegExp,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.cookieConsentAccepted$ = this.cookieConsent.accepted$;
    this.scrollStrategy = scrollStrategies.block();
    this.cookieConsent$ = cookieConsentFacade.accepted$;
  }

  ngOnInit() {
    this.searchHistory = [];

    if (isPlatformBrowser(this.platformId)) {
      this.searchHistory = JSON.parse(localStorage.getItem('searchHistory_' + this.cacheKey) ?? '[]');
      this.searchHistory$.next(this.searchHistory);
      this.searchHistory$
        .pipe(takeUntil(this.unsubscribe), withLatestFrom(this.cookieConsentAccepted$))
        .subscribe(([searchHistory, cookieConsentAccepted]) => {
          if (cookieConsentAccepted) {
            localStorage.setItem(
              'searchHistory_' + this.cacheKey,
              JSON.stringify(searchHistory.filter((term) => term !== ''))
            );
          } else {
            localStorage.removeItem('searchHistory_' + this.cacheKey);
          }
        });
    }

    this.searchField.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((inputValue) => {
      if (inputValue && inputValue.length > 1) {
        this.searchFieldChange(inputValue);
        this.searchTerm = inputValue;
      } else {
        this.options = [];
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['submittedSearchTerm']) {
      setTimeout(() => {
        this.searchTerm = changes['submittedSearchTerm'].currentValue;
      });
    }

    if (changes['sidebarOverlayToggled']) {
      this.overlayIsOpen = false;
    }
  }

  ngOnDestroy() {
    this.removeNoScrollClass();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  searchFieldChange(searchInput: string) {
    this.searchFieldChanged.emit(searchInput);
  }

  onSelectMobileAutocomplete(option: string) {
    this.removeNoScrollClass();
    this.overlayIsOpen = false;
    this.searchTerm = option;
    this.searchFieldSubmitted.emit(this.searchTerm);
  }

  onSubmit(event?: Event) {
    this.searchHistory = this.searchHistory
      .filter((term) => term.toLowerCase() !== this.searchTerm.toLowerCase())
      .slice(0, 9);

    if (this.searchTerm.trim() && this.emptySearchRegex.test(this.searchTerm)) {
      this.searchHistory.unshift(this.searchTerm);
    }

    this.searchHistory$.next(this.searchHistory);

    if (this.blurInput) {
      this.searchFieldInput?.nativeElement.blur();
    }
    this.removeNoScrollClass();
    this.searchFieldSubmitted.emit(this.searchTerm);
    event?.stopPropagation();
  }

  clearInputField() {
    this.searchField.setValue('');
    this.searchFieldCleared.emit(true);
  }

  triggerSearchOverlay() {
    this.overlayIsOpen = !this.overlayIsOpen;
    if (this.overlayIsOpen) {
      this.renderer.addClass(this.document.body, 'noScroll');
    } else {
      this.removeNoScrollClass();
    }
  }

  removeNoScrollClass() {
    this.renderer.removeClass(this.document.body, 'noScroll');
  }

  identifyOption(index: number, option: string) {
    return option || index;
  }
}
